import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { Button, Card, Input, Empty, Spinner } from "@/components/ui/Primitives";
import { api, formatErr } from "@/lib/api";
import { Plus, Pencil, Trash2, X } from "lucide-react";

const empty = { nome: "", cpf: "", telefone: "", cidade: "", observacoes: "" };

export default function Suppliers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/suppliers"); setItems(data); }
    catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); setErr(""); };
  const openEdit = (s) => { setEditing(s); setForm({ nome: s.nome || "", cpf: s.cpf || "", telefone: s.telefone || "", cidade: s.cidade || "", observacoes: s.observacoes || "" }); setOpen(true); setErr(""); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      if (editing) await api.put(`/suppliers/${editing.id}`, form);
      else await api.post("/suppliers", form);
      setOpen(false); await load();
    } catch (e2) { setErr(formatErr(e2)); }
    finally { setBusy(false); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Excluir fornecedor?")) return;
    try { await api.delete(`/suppliers/${id}`); await load(); }
    catch (e) { alert(formatErr(e)); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <Layout
      title="Fornecedores"
      subtitle="Cadastro de produtores e parceiros"
      actions={<Button onClick={openNew} data-testid="new-supplier-button"><Plus size={16} /> Novo Fornecedor</Button>}
    >
      {loading ? (
        <div className="flex justify-center py-20"><Spinner size={28} /></div>
      ) : items.length === 0 ? (
        <Card><Empty title="Nenhum fornecedor cadastrado" hint="Adicione seu primeiro fornecedor." /></Card>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 border-b border-ink-200">
              <tr className="text-left text-xs font-bold uppercase tracking-wider text-ink-500">
                <th className="px-4 py-3">Nome</th>
                <th className="px-4 py-3">CPF/CNPJ</th>
                <th className="px-4 py-3">Telefone</th>
                <th className="px-4 py-3">Cidade</th>
                <th className="px-4 py-3 w-1">Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id} className="border-b border-ink-200 last:border-0 hover:bg-stone-50 transition-colors" data-testid={`supplier-row-${s.id}`}>
                  <td className="px-4 py-3 font-medium text-ink-900">{s.nome}</td>
                  <td className="px-4 py-3 font-mono text-ink-700">{s.cpf || "—"}</td>
                  <td className="px-4 py-3 text-ink-700">{s.telefone || "—"}</td>
                  <td className="px-4 py-3 text-ink-700">{s.cidade || "—"}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <button onClick={() => openEdit(s)} className="p-1.5 rounded-md hover:bg-stone-100 text-ink-700" data-testid={`supplier-edit-${s.id}`}><Pencil size={16} /></button>
                    <button onClick={() => onDelete(s.id)} className="p-1.5 rounded-md hover:bg-red-100 text-danger" data-testid={`supplier-delete-${s.id}`}><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-stone-900/50 backdrop-blur-sm flex items-center justify-center p-4 print:hidden">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg animate-fade-in">
            <div className="flex items-center justify-between px-6 py-4 border-b border-ink-200">
              <h3 className="font-heading font-bold text-ink-900">{editing ? "Editar Fornecedor" : "Novo Fornecedor"}</h3>
              <button onClick={() => setOpen(false)} className="text-ink-500 hover:text-ink-900"><X size={18} /></button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4">
              <Input label="Nome" required value={form.nome} onChange={set("nome")} data-testid="supplier-nome" />
              <div className="grid grid-cols-2 gap-3">
                <Input label="CPF/CNPJ" value={form.cpf} onChange={set("cpf")} data-testid="supplier-cpf" />
                <Input label="Telefone" value={form.telefone} onChange={set("telefone")} data-testid="supplier-telefone" />
              </div>
              <Input label="Cidade" value={form.cidade} onChange={set("cidade")} data-testid="supplier-cidade" />
              <Input label="Observações" value={form.observacoes} onChange={set("observacoes")} />
              {err && <div className="text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md">{err}</div>}
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button type="submit" disabled={busy} data-testid="supplier-submit">{busy ? <Spinner size={16} /> : "Salvar"}</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
